import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import { role_dict } from "@screens/sampleData";
import Button from "@components/ui/buttons/Button";
import { useState, useEffect, useRef } from "react";
import TextField from "@components/ui/textfields/TextField";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";
import DatePicker from "@components/ui/selection/DatePicker";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import MemberDocuments from "@components/ui/blocks/MemberDocuments";
import { isoToSeconds, secondsToIso } from "@utils/isoDate";
import { MemberItem } from "@components/ui/blocks/MemberAssignmentBlock";
import { User } from "types/user";
import { useDataStore } from "@store/user_org";
import { AccessType, isNoLoginMember, isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { usersApi } from "@services/api/users";
import { invitationsApi } from "@services/api/invitations";
import ConfirmationModal from "@components/support/ConfirmationModal";
import {
  CustomAvatar,
  TOTAL_SHAPES,
  TOTAL_COLORS,
  BACKGROUND_COLORS,
  parseCustomAvatar,
} from "@components/ui/CustomAvatar";

export const MemberModal = ({
  onClose,
  memberId,
}: {
  onClose: () => void;
  memberId: string;
}) => {
  const orgMembers = useDataStore((state) => state.orgMembers);
  const userData = useDataStore((state) => state.userData);
  const updateUserData = useDataStore((state) => state.updateUserData);
  const fetchOrgMembers = useDataStore((state) => state.fetchOrgMembers);
  const fetchUserData = useDataStore((state) => state.fetchUserData);

  const originalMember = orgMembers.find((u) => u.id === memberId);

  const [memberData, setMemberData] = useState<User | null>(
    originalMember || null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);

  const [isUploading, setIsUploading] = useState(false);
  const [isResendingInvitation, setIsResendingInvitation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEmailEdit, setShowEmailEdit] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Color palette for user tags
  const colorPalette = [
    "#A5B4FC", // indigo-300
    "#818CF8", // indigo-400
    "#C4B5FD", // violet-300
    "#A78BFA", // violet-400
    "#F9A8D4", // pink-300
    "#FDA4AF", // rose-300
    "#FCA5A5", // red-300
    "#FDBA74", // orange-300
    "#FCD34D", // amber-300
    "#FDE047", // yellow-300
    "#BEF264", // lime-300
    "#86EFAC", // green-300
    "#6EE7B7", // emerald-300
    "#5EEAD4", // teal-300
    "#67E8F9", // cyan-300
    "#7DD3FC", // sky-300
    "#93C5FD", // blue-300
    "#CBD5E1", // slate-300
    "#94A3B8", // slate-400
    "#D6D3D1", // stone-300
    "#A8A29E", // stone-400
  ];

  // Reload the card when the member really changed server-side. The presence listener
  // rewrites orgMembers each time ANY colleague's activity ticks: reloading on that wiped
  // what was being typed (stagiaire comment, unsaved dates → the block vanished, 2026-10-08).
  // Once the card has been edited, nothing from the store overwrites it until it is saved
  // or closed (the name being typed was still erased, 2026-10-08).
  const lastLoadedRef = useRef<string>("");
  const loadedMemberRef = useRef<User | null>(originalMember || null);
  useEffect(() => {
    const updatedMember = orgMembers.find((u) => u.id === memberId);
    if (!updatedMember) return;
    const { activity: _activity, ...rest } = updatedMember as User & { activity?: unknown };
    const key = JSON.stringify(rest);
    if (key === lastLoadedRef.current) return;
    lastLoadedRef.current = key;
    setMemberData((current) => {
      const edited = current !== null && current !== loadedMemberRef.current;
      if (edited) return current;
      loadedMemberRef.current = updatedMember;
      return updatedMember;
    });
  }, [orgMembers, memberId]);

  const handleUpdateMemberEmail = async () => {
    if (!memberData || !newMemberEmail.trim()) return;
    setIsUpdatingEmail(true);
    try {
      await usersApi.updateEmail(memberData.id, newMemberEmail.trim());
      if (userData?.org_id) await fetchOrgMembers(userData.org_id);
      if (memberData.id === userData?.id) await fetchUserData(userData!.id);
      showNotification({ type: "success", message: "Adresse e-mail mise à jour avec succès" });
      setShowEmailEdit(false);
      setNewMemberEmail("");
    } catch (error: any) {
      const detail = error?.response?.data?.detail || "Échec de la mise à jour de l'adresse e-mail";
      showNotification({ type: "error", message: detail });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleMemberPasswordReset = async () => {
    if (!memberData) return;
    setIsSendingPasswordReset(true);
    try {
      await usersApi.sendPasswordReset(memberData.id);
      showNotification({
        type: "success",
        message: `Un e-mail de réinitialisation a été envoyé à ${memberData.infos.email}`,
      });
      showNotification({
        type: "warning",
        message: "Attention : le lien de réinitialisation expire dans 59 minutes.",
      });
    } catch {
      showNotification({ type: "error", message: "Échec de l'envoi de l'e-mail de réinitialisation" });
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  const handleSave = async () => {
    if (!memberData) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      // L'« Équipe atelier » affichée par défaut est enregistrée : sans elle, le planning
      // déduisait le métier du rôle, et un changement de rôle reclassait le compagnon.
      const isWorkshopRole = ["mecanicien", "carrossier", "chef_atelier"].includes(memberData.role);
      const config = isWorkshopRole && !memberData.config.team
        ? { ...memberData.config, team: (memberData.role === "carrossier" ? "carrosserie" : "mecanique") as "mecanique" | "carrosserie" }
        : memberData.config;
      await updateUserData(memberData.id, {
        infos: memberData.infos,
        config,
        role: memberData.role,
        customization: memberData.customization,
      });

      // Refresh org members
      if (userData?.org_id) {
        await fetchOrgMembers(userData.org_id);
      }

      // Refetch user data if updating own profile
      if (memberData.id === userData?.id) {
        await fetchUserData(userData.id);
      }

      showNotification({
        type: "success",
        message: "Modifications enregistrées avec succès",
      });
      onClose();
    } catch (error: any) {
      console.error("Failed to update user:", error);
      const errorMessage = "Échec de l'enregistrement des modifications";
      setSaveError(errorMessage);
      showNotification({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !memberData) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showNotification({
        type: "error",
        message: "Type de fichier invalide. Formats acceptés : JPG, PNG, WEBP",
      });
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification({
        type: "error",
        message: "La taille du fichier dépasse 5MB",
      });
      return;
    }

    setIsUploading(true);

    try {
      const { compressImage } = await import("@utils/compressImage");
      const compressed = await compressImage(file, 512);
      const result = await usersApi.uploadProfilePicture(memberData.id, compressed);

      // Update local state with new photo URL
      setMemberData({
        ...memberData,
        customization: {
          ...memberData.customization,
          photo_url: result.photo_url,
        },
      });

      // Refresh org members
      if (userData?.org_id) {
        await fetchOrgMembers(userData.org_id);
      }

      // Refetch user data if updating own profile
      if (memberData.id === userData?.id) {
        await fetchUserData(userData.id);
      }

      showNotification({
        type: "success",
        message: "Photo de profil mise à jour avec succès",
      });
    } catch (error: any) {
      console.error("Failed to upload profile picture:", error);
      showNotification({
        type: "error",
        message: "Échec du téléchargement de la photo de profil",
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleResendInvitation = async () => {
    if (!memberData) return;

    setIsResendingInvitation(true);
    try {
      await invitationsApi.resend(memberData.id);
      showNotification({
        type: "success",
        message: "L'invitation a été renvoyée avec succès",
      });
    } catch (error: any) {
      console.error("Failed to resend invitation:", error);
      showNotification({
        type: "error",
        message: "Impossible de renvoyer l'invitation. Veuillez réessayer.",
      });
    } finally {
      setIsResendingInvitation(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!memberData) return;

    setIsDeleting(true);
    try {
      await usersApi.delete(memberData.id);

      // Refresh org members
      if (userData?.org_id) {
        await fetchOrgMembers(userData.org_id);
      }

      showNotification({
        type: "success",
        message: "Le compte a été supprimé avec succès",
      });
      onClose();
    } catch (error: any) {
      console.error("Failed to delete user:", error);
      showNotification({
        type: "error",
        message: "Échec de la suppression du compte",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const canEdit = isOwnerOrAdmin(userData) || userData?.id === memberId;
  // Stagiaire added without e-mail: technical address, no login → no e-mail/password actions.
  const noLogin = isNoLoginMember(memberData?.infos.email);

  // Several internship periods on the same member (the garage reuses one "stagiaire" member
  // for every intern). Older cards only have stagiaire_from/to: shown as the first period.
  const stagiairePeriods: { from: string; to: string }[] =
    memberData?.config.stagiaire_periods?.length
      ? memberData.config.stagiaire_periods
      : [{ from: memberData?.config.stagiaire_from || "", to: memberData?.config.stagiaire_to || "" }];
  const setPeriods = (periods: { from: string; to: string }[]) => {
    if (!memberData) return;
    setMemberData({
      ...memberData,
      config: { ...memberData.config, stagiaire_periods: periods, stagiaire_from: "", stagiaire_to: "" },
    });
  };
  return (
    <BaseModal
      title="Membre de l'équipe"
      startIcon={<Icon1 name="user" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-neutral-100 flex flex-col gap-2 p-4">
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Photo de profil</div>
          <div className="relative">
            <UserAvatar
              src={memberData?.customization.photo_url}
              className="w-24 h-24 mb-2 rounded-full border flex-shrink-0"
              value={
                memberData?.customization.avatar_seed ||
                // The SAVED name, not the one being typed: the generated avatar was redrawn on
                // every keystroke. Same seed as the rest of the app shows for this member.
                orgMembers.find((u) => u.id === memberId)?.infos.display_name ||
                memberData?.infos.display_name
              }
              size={96}
            />
            {canEdit && (
              <SecondaryButton
                startIcon={<Icon1 name="join" size={18}></Icon1>}
                label="Changer de photo"
                variant="large"
                onClick={() => setShowPhotoSelector(!showPhotoSelector)}
              ></SecondaryButton>
            )}
          </div>

          {showPhotoSelector && canEdit && (() => {
            const parsed = parseCustomAvatar(memberData?.customization.avatar_seed);
            const selectedShape = parsed?.shapeKey ?? 1;
            const selectedColorIdx = parsed?.colorIndex ?? 0;

            const setAvatar = (shapeKey: number, colorIndex: number) => {
              if (memberData) {
                setMemberData({
                  ...memberData,
                  customization: {
                    ...memberData.customization,
                    photo_url: "",
                    avatar_seed: `custom:${shapeKey}:${colorIndex}`,
                  },
                });
              }
            };

            return (
              <div className="flex flex-col gap-2 mt-2">
                <div className="text-h3 font-semibold text-neutral-600">
                  Choisir une forme
                </div>
                <div className="flex flex-row gap-2 flex-wrap">
                  {Array.from({ length: TOTAL_SHAPES }, (_, i) => {
                    const shapeKey = i + 1;
                    return (
                      <button
                        key={shapeKey}
                        onClick={() => setAvatar(shapeKey, selectedColorIdx)}
                        className={`rounded-full border-2 transition-all overflow-hidden ${
                          !memberData?.customization.photo_url && selectedShape === shapeKey
                            ? "border-blue-500 ring-2 ring-blue-200"
                            : "border-neutral-200 hover:border-neutral-400"
                        }`}
                      >
                        <CustomAvatar shapeKey={shapeKey} colorIndex={selectedColorIdx} size={40} />
                      </button>
                    );
                  })}
                </div>
                <div className="text-h3 font-semibold text-neutral-600">
                  Choisir une couleur
                </div>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: TOTAL_COLORS }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setAvatar(selectedShape, i)}
                      className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${
                        !memberData?.customization.photo_url && selectedColorIdx === i
                          ? "ring-2 ring-offset-1 ring-black"
                          : "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300"
                      }`}
                      style={{ backgroundColor: `#${BACKGROUND_COLORS[i]}` }}
                    />
                  ))}
                </div>
                <div className="text-h3 text-neutral-400">ou</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <SecondaryButton
                  startIcon={<Icon1 name="join" size={18} />}
                  label={
                    isUploading ? "Téléchargement..." : "Télécharger une photo"
                  }
                  variant="large"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                />
                {memberData?.customization.photo_url && (
                  <SecondaryButton
                    startIcon={<Icon1 name="trash" size={18} />}
                    label="Utiliser l'avatar généré"
                    variant="large"
                    onClick={() => {
                      if (memberData) {
                        setMemberData({
                          ...memberData,
                          customization: {
                            ...memberData.customization,
                            photo_url: "",
                            avatar_seed:
                              memberData.customization.avatar_seed ||
                              "custom:1:0",
                          },
                        });
                      }
                    }}
                  />
                )}
              </div>
            );
          })()}
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <TextField
            label="Pseudo/Prénom (court)"
            value={memberData?.infos.display_name || ""}
            onChange={(value) => {
              if (memberData) {
                setMemberData({
                  ...memberData,
                  infos: { ...memberData.infos, display_name: value },
                });
              }
            }}
            placeholder="Marie C."
            disabled={!canEdit}
          />
          <TextField
            label="Nom complet"
            value={memberData?.infos.full_name || ""}
            onChange={(value) => {
              if (memberData) {
                setMemberData({
                  ...memberData,
                  infos: { ...memberData.infos, full_name: value },
                });
              }
            }}
            placeholder="Marie Curie"
            disabled={!canEdit}
          />
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <DropDown
                options={Object.entries(role_dict).map(([key, value]) => ({
                  value: key,
                  label: value,
                }))}
                label="Rôle"
                value={memberData?.role || Object.keys(role_dict)[0]}
                onSelect={(value) => {
                  setMemberData({
                    ...(memberData as User),
                    role: String(value),
                  });
                }}
                disabled={!isOwnerOrAdmin(userData)}
              ></DropDown>
            </div>
            <div className="flex-1">
              <DropDown
                options={[
                  { value: "OWNER", label: "Propriétaire" },
                  { value: "ADMIN", label: "Manager" },
                  { value: "MEMBER", label: "Membre" },
                  { value: "GUEST", label: "Invité" },
                ]}
                label="Accès"
                value={memberData?.config.access_type || "MEMBER"}
                onSelect={(value) => {
                  if (memberData) {
                    setMemberData({
                      ...memberData,
                      config: {
                        ...memberData.config,
                        access_type: String(value) as AccessType,
                      },
                    });
                  }
                }}
                disabled={!isOwnerOrAdmin(userData)}
              ></DropDown>
            </div>
          </div>

          {memberData?.role && ["mecanicien", "carrossier", "chef_atelier"].includes(memberData.role) && (
            <DropDown
              options={[
                { value: "mecanique", label: "Mécanique" },
                { value: "carrosserie", label: "Carrosserie" },
              ]}
              label="Équipe atelier"
              value={memberData?.config.team || (memberData?.role === "carrossier" ? "carrosserie" : "mecanique")}
              onSelect={(value) => {
                if (memberData) {
                  setMemberData({
                    ...memberData,
                    config: {
                      ...memberData.config,
                      team: String(value) as "mecanique" | "carrosserie",
                    },
                  });
                }
              }}
              disabled={!isOwnerOrAdmin(userData)}
            />
          )}
          {memberData?.role && ["mecanicien", "carrossier", "chef_atelier"].includes(memberData.role) && (
            <div className="flex flex-col gap-2">
              <TextField
                label="Ordre au planning atelier (1 = première colonne)"
                type="number"
                value={memberData.config.atelier_order ? String(memberData.config.atelier_order) : ""}
                placeholder="Automatique"
                onChange={(v) => {
                  const n = parseInt(String(v), 10);
                  setMemberData({ ...memberData, config: { ...memberData.config, atelier_order: Number.isFinite(n) && n > 0 ? n : 0 } });
                }}
                disabled={!isOwnerOrAdmin(userData)}
              />
              <CheckBoxInput
                label="Stagiaire : visible au planning atelier, hors prise de RDV"
                checked={!!memberData.config.stagiaire}
                disabled={!isOwnerOrAdmin(userData)}
                onChange={(e) => setMemberData({ ...memberData, config: { ...memberData.config, stagiaire: e.target.checked } })}
              />
              {memberData.config.stagiaire && (
                <div className={`flex flex-col gap-2 ${isOwnerOrAdmin(userData) ? "" : "pointer-events-none opacity-60"}`}>
                  {stagiairePeriods.map((p, i) => (
                    <div key={i} className="flex flex-row gap-2 items-end">
                      <div className="flex-1">
                        <DatePicker
                          label={stagiairePeriods.length > 1 ? `Période ${i + 1} — du` : "Du"}
                          seconds={isoToSeconds(p.from)}
                          onChange={(sec) => setPeriods(stagiairePeriods.map((x, j) => (j === i ? { ...x, from: secondsToIso(sec) } : x)))}
                        />
                      </div>
                      <div className="flex-1">
                        <DatePicker
                          label="Au"
                          seconds={isoToSeconds(p.to)}
                          minDateSeconds={isoToSeconds(p.from)}
                          onChange={(sec) => setPeriods(stagiairePeriods.map((x, j) => (j === i ? { ...x, to: secondsToIso(sec) } : x)))}
                        />
                      </div>
                      {stagiairePeriods.length > 1 && (
                        <button
                          title="Retirer cette période"
                          className="mb-3 text-neutral-400 hover:text-red-600"
                          onClick={() => setPeriods(stagiairePeriods.filter((_, j) => j !== i))}
                        >
                          <Icon1 name="trash" size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  <SecondaryButton
                    variant="small"
                    label="Ajouter une période"
                    onClick={() => setPeriods([...stagiairePeriods, { from: "", to: "" }])}
                  />
                </div>
              )}
              {memberData.config.stagiaire && isOwnerOrAdmin(userData) && (
                <>
                  <AreaTextField
                    label="Commentaire (niveau du jeune…)"
                    value={memberData.config.stagiaire_note || ""}
                    onChange={(v) => setMemberData({ ...memberData, config: { ...memberData.config, stagiaire_note: String(v ?? "") } })}
                    rows={3}
                  />
                  <MemberDocuments userId={memberData.id} />
                </>
              )}
            </div>
          )}
          {memberData && isOwnerOrAdmin(userData) && (
            <div className="flex flex-row gap-2 items-end">
              <div className="flex-1">
                {/* Fin de contrat : plus au planning ni dans la prise de RDV après ce jour (Mélanie, 2026-10-08). */}
                <DatePicker
                  label="Date de sortie (dernier jour travaillé)"
                  seconds={isoToSeconds(memberData.config.exit_date)}
                  onChange={(sec) => setMemberData({ ...memberData, config: { ...memberData.config, exit_date: secondsToIso(sec) } })}
                />
              </div>
              {memberData.config.exit_date && (
                <button
                  title="Retirer la date de sortie"
                  className="mb-3 text-neutral-400 hover:text-red-600"
                  onClick={() => setMemberData({ ...memberData, config: { ...memberData.config, exit_date: "" } })}
                >
                  <Icon1 name="trash" size={16} />
                </button>
              )}
            </div>
          )}
          {!noLogin && (
            <TextField
              label="Adresse e-mail"
              placeholder="marie.curie@life.com"
              value={memberData?.infos.email || ""}
              disabled={true}
            />
          )}
          <TextField
            label="Numéro de téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={memberData?.infos.phone_number || ""}
            onChange={(value) => {
              if (memberData) {
                setMemberData({
                  ...memberData,
                  infos: { ...memberData.infos, phone_number: value },
                });
              }
            }}
            disabled={!canEdit}
          />
          <TextField
            label="UID"
            placeholder="uid_1234567890"
            value={memberData?.id}
            disabled={true}
          />
        </div>
        <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
          <div className="text-h4 text-neutral-400">Drapeau personnel</div>
          <MemberItem
            memberName={memberData?.infos.display_name || "Utilisateur"}
            memberColor={memberData?.customization.color_tag || "#000000"}
          ></MemberItem>
          {canEdit && (
            <div className="flex flex-wrap gap-1">
              {colorPalette.map((color) => (
                <button
                  key={color}
                  onClick={() => {
                    if (memberData) {
                      setMemberData({
                        ...memberData,
                        customization: {
                          ...memberData.customization,
                          color_tag: color,
                        },
                      });
                    }
                  }}
                  className={`w-8 h-8 rounded-full transition-all hover:scale-110 ${
                    memberData?.customization.color_tag === color
                      ? "ring-2 ring-offset-1 ring-black"
                      : "hover:ring-2 hover:ring-offset-1 hover:ring-neutral-300"
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          )}
        </div>
        {isOwnerOrAdmin(userData) && (
          <div className="flex flex-col gap-2 bg-white p-4 rounded-lg border-1">
            {!noLogin && (<>
            <SecondaryButton
              label="Modifier l'adresse email"
              onClick={() => { setShowEmailEdit(!showEmailEdit); setNewMemberEmail(""); }}
            />
            {showEmailEdit && (
              <div className="flex flex-col gap-2 pl-1">
                <div className="text-h5 text-neutral-400">
                  Actuelle : {memberData?.infos.email}
                </div>
                <TextField
                  label="Nouvelle adresse e-mail"
                  value={newMemberEmail}
                  onChange={(v) => setNewMemberEmail(v)}
                  placeholder="nouvelle@email.com"
                  type="email"
                />
                <div className="flex flex-row gap-2 justify-end">
                  <SecondaryButton
                    label="Annuler"
                    variant="small"
                    onClick={() => { setShowEmailEdit(false); setNewMemberEmail(""); }}
                  />
                  <Button
                    label={isUpdatingEmail ? "Mise à jour..." : "Confirmer"}
                    variant="small"
                    onClick={handleUpdateMemberEmail}
                    disabled={isUpdatingEmail || !newMemberEmail.trim()}
                  />
                </div>
              </div>
            )}
            <SecondaryButton
              label={isSendingPasswordReset ? "Envoi en cours..." : "Réinitialiser le mot de passe (par e-mail)"}
              onClick={handleMemberPasswordReset}
              disabled={isSendingPasswordReset}
            />
            </>)}
            {memberData?.status === "pending" && (
              <SecondaryButton
                label={
                  isResendingInvitation
                    ? "Envoi en cours..."
                    : "Renvoyer une invitation"
                }
                disabled={isResendingInvitation}
                onClick={handleResendInvitation}
              />
            )}
            <SecondaryButton
              danger={true}
              label={isDeleting ? "Suppression..." : "Supprimer le compte"}
              disabled={isDeleting}
              onClick={() => setShowDeleteConfirmation(true)}
            ></SecondaryButton>
          </div>
        )}
      </div>
      {saveError && (
        <div className="px-4 text-h4 text-red-500">{saveError}</div>
      )}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={isSaving ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={isSaving || !canEdit}
        ></Button>
      </div>
      {showDeleteConfirmation && (
        <ConfirmationModal
          title={`Supprimer le compte de ${memberData?.infos.display_name || memberData?.infos.email} ?`}
          message="Cette action supprimera définitivement le compte de cet utilisateur. Il ne pourra plus se connecter à la plateforme."
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
    </BaseModal>
  );
};
